import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import {
  VacationRequest,
  RequestDay,
  SimplifiedVacationRequest,
} from '../../../interfaces/holidays.interface';
import { MatIcon } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { StatusHistoryDialogComponent } from '../status-history-dialog/status-history-dialog.component';
import { inject, output } from '@angular/core';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import { VacationRequestDialogComponent } from '../vacation-request-dialog/vacation-request-dialog.component';

@Component({
  selector: 'app-driver-petitions-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIcon,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDialogModule,
    MatTooltipModule,
  ],
  templateUrl: './driver-petitions-table.component.html',
  styleUrls: [
    './driver-petitions-table.component.css',
    '../../../styles/layout.css',
  ],
})
export class DriverPetitionsTableComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() requests: SimplifiedVacationRequest[] = [];

  displayedColumns: string[] = [
    'request_type',
    'status',
    'request_days',
    // 'created_when', // Removed - not available in SimplifiedVacationRequest
    'acciones',
    'reject',
  ];
  dataSource: SimplifiedVacationRequest[] = [];
  statusFilter = new FormControl<string[]>([]);
  statusList = [
    { value: 'approved', label: 'Aprobado' },
    { value: 'rejected', label: 'Rechazado' },
    { value: 'cancelled', label: 'Cancelado' },
  ];

  typeFilter = new FormControl<string[]>([]);
  typeList = [
    { value: 'holidays', label: 'Vacaciones' },
    { value: 'personal_day', label: 'Día Personal' },
  ];

  private destroy$ = new Subject<void>();

  petitionRejected = output<void>();
  private readonly holidaysService = inject(HolidaysService);

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.updateDataSource();
    this.statusFilter.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDataSource();
      });

    this.typeFilter.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDataSource();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requests']) {
      this.updateDataSource();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDataSource(): void {
    let filteredRequests = [...this.requests];

    // Apply status filter
    const selectedStatuses = this.statusFilter.value;
    if (selectedStatuses && selectedStatuses.length > 0) {
      filteredRequests = filteredRequests.filter((request) =>
        selectedStatuses.includes(request.status)
      );
    }

    // Apply type filter
    const selectedTypes = this.typeFilter.value;
    if (selectedTypes && selectedTypes.length > 0) {
      filteredRequests = filteredRequests.filter((request) =>
        selectedTypes.includes(request.request_type)
      );
    }

    // Sort by request ID descending (most recent first)
    filteredRequests.sort((a, b) => b.id - a.id);

    this.dataSource = filteredRequests;
  }

  getRequestTypeDisplay(requestType: string): string {
    return requestType === 'holidays' ? 'Vacaciones' : 'Día Personal';
  }

  getStatusDisplay(status: string): string {
    const statusMap: { [key: string]: string } = {
      cancelled: 'Cancelado',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      denied: 'Rechazado',
    };
    return statusMap[status] || status;
  }

  getRequestDaysDisplay(request: SimplifiedVacationRequest): string {
    if (!request || !request.start_date || !request.end_date) {
      return 'N/A';
    }

    const daysLabel = request.duration_days === 1 ? 'día' : 'días';

    if (request.duration_days === 1 || request.start_date === request.end_date) {
      return `${request.start_date} (1 ${daysLabel})`;
    }

    return `${request.start_date} - ${request.end_date} (${request.duration_days} ${daysLabel})`;
  }

  viewDetails(request: SimplifiedVacationRequest): void {
    if (!request.status_history || request.status_history.length === 0) {
      console.warn('No status history available for this request');
      return;
    }

    this.dialog.open(StatusHistoryDialogComponent, {
      width: '900px',
      minWidth: '700px',
      data: { request },
    });
  }

  private formatDate(dateStr: string): string {
    // Convert YYYY-MM-DD to DD/MM/YYYY
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  rejectPetition(request: SimplifiedVacationRequest) {
    
    if (!request.uuid) {
      return;
    }

    const dialogRef = this.dialog.open(VacationRequestDialogComponent, {
      data: {
        title: 'Rechazar petición',
        message: '¿Está seguro que desea rechazar esta petición de vacaciones?',
        confirmText: 'Rechazar',
        cancelText: 'Cancelar',
        toastSuccessMessage: 'Petición rechazada correctamente',
        toastErrorMessage: 'Error al rechazar la petición',
        reject: true,
        handler: (comment?: string) =>
          this.holidaysService.rejectPetition(request.uuid, comment),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.petitionRejected.emit();
      }
    });
  }


}
